/* eslint-disable max-lines, camelcase */
import {
    TrackingContact,
    TrackingError,
    TrackingInformation,
    TrackingPageView,
} from '../interfaces/tracking';
import {
    trackIt,
    TRACKING_INFORMATION_ACTION_REVEAL,
    TRACKING_INFORMATION_TRIGGER_ACCORDION,
    TRACKING_TYPE_INFORMATION,
    TRACKING_TYPE_PAGEVIEW,
    TRACKING_ERROR_TYPE_TECHNICAL,
    TRACKING_TYPE_ERROR,
    TRACKING_ERROR_TYPE_VALIDATION,
} from '@vfde-sails/tracking';
import {
    TRACKING_CAPTCHA_SHOWN,
    TRACKING_CONTACT_ACTION_ENGAGE,
    TRACKING_CONTACT_SUBJECT_FIBER,
    TRACKING_CONTACT_TOOL,
    TRACKING_CONTACT_TRIGGER_AUTO,
    TRACKING_CONTACT_TRIGGER_BUTTON,
    TRACKING_ERROR_ACTION,
    TRACKING_INFORMATION_TRIGGER_ICON,
    TRACKING_INFORMATION_TRIGGER_REFERENCE,
    TRACKING_INFORMATION_UI_TYPE_FOOTNOTE,
    TRACKING_INFORMATION_UI_TYPE_TOOLTIP,
    TRACKING_TYPE_CONTACT,
} from '../constants';
import { formatToTrackingString } from './formatter';
import { debugInfo } from './debug';
import { TrackingAvcStep } from '../enums/tracking';
import { TOOLTIP_CLASSNAME_TRIGGER_ELEMENT } from '@vfde-brix/ws10/tooltip';
import {
    getAVCPageView,
    getGeneralPageView,
    getTrackingOrder,
} from './trackingHelper';
import { ApiErrorResponseWithCode } from '@vfde-sails/utils';
import { AvcResult, JarvisProduct } from '@vfde-sails/avc-core';
import { AccordionType } from '../components/accordion';
import { HomesPassedType } from '../enums/product';

const trackTooltip = function (this: HTMLElement) {
    try {
        const tooltipText = this.parentElement?.innerText || '';

        const payload: TrackingInformation = {
            information_action: TRACKING_INFORMATION_ACTION_REVEAL,
            information_name: formatToTrackingString(tooltipText),
            information_ui_type: TRACKING_INFORMATION_UI_TYPE_TOOLTIP,
            information_trigger: TRACKING_INFORMATION_TRIGGER_ICON,
        };

        debugInfo('Track tooltip: ', payload);
        trackIt(TRACKING_TYPE_INFORMATION, payload);
    } catch (e) {
        debugInfo('Track tooltip failed: ', e);
    }
};

export const addTooltipTracking = (element: Element | string) => {
    const tooltip = element instanceof Element ? element : document.getElementById(element || '');

    const [tooltipIcon] = tooltip?.parentElement?.getElementsByClassName(
        TOOLTIP_CLASSNAME_TRIGGER_ELEMENT
    ) || [];

    // TODO: This is not a pure function, consider refactoring
    tooltipIcon?.removeEventListener('mouseover', trackTooltip);
    tooltipIcon?.addEventListener('mouseover', trackTooltip, { once: true });
};
/**
 * Track accordion
 * @param text trackAccordion
 * @param type
 */
export const trackAccordion = (text: string, type: AccordionType) => {
    try {
        const payload: TrackingInformation = {
            information_action: TRACKING_INFORMATION_ACTION_REVEAL,
            information_name: formatToTrackingString(text),
            information_ui_type: type,
            information_trigger: TRACKING_INFORMATION_TRIGGER_ACCORDION,
        };

        debugInfo('Track accordion: ', payload);
        trackIt(TRACKING_TYPE_INFORMATION, payload);
    } catch (e) {
        debugInfo('Track accordion failed: ', e);
    }
};

export const trackPageView = (
    selectedTariff: JarvisProduct,
    avcResult?: AvcResult,
    avcStep?: TrackingAvcStep,
    accessSupplier?: string,
    availabilityIndicator?: string,
    homePassed?: HomesPassedType
) => {
    try {
        if (!selectedTariff) { return; }
        const hotlineNumbers = (window as any)?.VF['new-flyout']?.hotlineNumbers;

        const payload: TrackingPageView = {
            ...getGeneralPageView(),
            ...avcStep
                ? getAVCPageView(selectedTariff, avcResult,
                    avcStep, accessSupplier, availabilityIndicator, homePassed)
                : { oOrder: getTrackingOrder(selectedTariff) },
            ...hotlineNumbers && { hotline_numbers: hotlineNumbers },
        };

        debugInfo('Track pageview event: ', payload);
        trackIt(TRACKING_TYPE_PAGEVIEW, payload);
    } catch (e) {
        debugInfo('Track pageview failed: ', e);
    }
};

export const trackLMFormOpen = () => {
    try {
        const payload: TrackingContact = {
            contact_action: TRACKING_INFORMATION_ACTION_REVEAL,
            contact_subject: TRACKING_CONTACT_SUBJECT_FIBER,
            contact_tool: TRACKING_CONTACT_TOOL,
            contact_trigger: TRACKING_CONTACT_TRIGGER_BUTTON,
        };

        debugInfo('Track contact open event: ', payload);
        trackIt(TRACKING_TYPE_CONTACT, payload);
    } catch (e) {
        debugInfo('Track contact open event failed: ', e);
    }
};

export const trackLMFormSubmit = () => {
    try {
        const payload: TrackingContact = {
            contact_action: TRACKING_CONTACT_ACTION_ENGAGE,
            contact_subject: TRACKING_CONTACT_SUBJECT_FIBER,
            contact_tool: TRACKING_CONTACT_TOOL,
            contact_trigger: TRACKING_CONTACT_TRIGGER_AUTO,
        };

        debugInfo('Track contact submit event: ', payload);
        trackIt(TRACKING_TYPE_CONTACT, payload);
    } catch (e) {
        debugInfo('Track contact submit event failed: ', e);
    }
};

export const trackFootnoteLink = (text: string) => {
    try {
        const payload = {
            information_action: TRACKING_INFORMATION_ACTION_REVEAL,
            information_name: formatToTrackingString(text),
            information_ui_type: TRACKING_INFORMATION_UI_TYPE_FOOTNOTE,
            information_trigger: TRACKING_INFORMATION_TRIGGER_REFERENCE,
        };

        debugInfo('Track footnote link: ', payload);
        trackIt(TRACKING_TYPE_INFORMATION, payload);
    } catch (e) {
        debugInfo('Track footnote link failed: ', e);
    }
};

export const trackError = (
    error: Error | ApiErrorResponseWithCode,
    isValidationError: boolean = false
) => {
    try {
        const payload: TrackingError = {
            error_action: TRACKING_ERROR_ACTION,
            error_type: isValidationError ? TRACKING_ERROR_TYPE_VALIDATION : TRACKING_ERROR_TYPE_TECHNICAL,
            error_message: formatToTrackingString(error.message),
            ...'status' in error && { error_code: error.status.toString() },
        };

        debugInfo('Track error event: ', payload);
        trackIt(TRACKING_TYPE_ERROR, payload);
    } catch (e) {
        debugInfo('Track error event failed: ', e);
    }
};

export const trackCaptcha = () => {
    try {
        debugInfo('Track Captcha: ', TRACKING_CAPTCHA_SHOWN);
        trackIt(TRACKING_CAPTCHA_SHOWN, {});
    } catch (e) {
        debugInfo('Track Captcha failed: ', e);
    }
};
